import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { ChatFeedbackAPI } from "@/utils/axios";

interface ChatFeedbackProps {
  conversationId: string;
  messageId: string;
  initial?: "like" | "dislike" | "redo" | null;
  onRedo?: () => void;
}

export const ChatFeedback: React.FC<ChatFeedbackProps> = ({
  conversationId,
  messageId,
  initial = null,
  onRedo,
}) => {
  const [selected, setSelected] = useState<"like" | "dislike" | "redo" | null>(
    initial
  );
  const [isSending, setIsSending] = useState(false);

  const handleSelect = async (value: "like" | "dislike" | "redo") => {
    if (isSending) return;
    setSelected(value);
    setIsSending(true);
    try {
      if (value === "redo" && onRedo) {
        onRedo(); // trigger immediately
        setSelected(null);
      }

      // Fire-and-forget feedback call
      ChatFeedbackAPI.sendFeedback({
        conversation_id: conversationId,
        message_id: messageId,
        feedback: value,
      }).catch((err) => console.error("Failed to send feedback", err));
    } catch (err) {
      console.error("Unexpected error", err);
    } finally {
      setIsSending(false);
    }
  };

  const commonClass = "h-4 w-4 cursor-pointer transition-colors duration-150";

  const upClass = cn(commonClass, selected === "like" && "text-green-600");
  const downClass = cn(commonClass, selected === "dislike" && "text-red-600");
  const redoClass = cn(commonClass, selected === "redo" && "text-yellow-600");

  return (
    <div className="flex space-x-1">
      <ThumbsUp
        className={upClass}
        onClick={() => handleSelect("like")}
        aria-label="Like response"
      />
      <ThumbsDown
        className={downClass}
        onClick={() => handleSelect("dislike")}
        aria-label="Dislike response"
      />
      <RotateCcw
        className={redoClass}
        onClick={() => handleSelect("redo")}
        aria-label="Request redo"
      />
    </div>
  );
};
